import Jsona from 'jsona';
import { get, isEmpty, keyBy, uniq } from 'lodash';

import {
  getFracFleets,
  getFracFleetWells,
  getInterventionUnits,
  getPadWells,
  getResolvedAssets,
  getRigs,
  getWells,
} from '~/clients/jsonApi';
import { DEV_CENTER_CLI_APP_ID } from '~constants';
import { ASSET_TYPES } from '~/constants/assetTypes';
import { getAppSegment } from '~/utils/devcenter';

const dataFormatter = new Jsona();

export const getAppAssetSpecificTypeId = (app, typeId) => +get(app, ['settings', typeId]);

export const getUniqAssetIds = (assetIds, cachedAssetIds) =>
  uniq(
    assetIds
      .map(([_appId, assetId]) => assetId)
      .filter(assetId => Number.isFinite(assetId) && !cachedAssetIds.includes(String(assetId)))
  );

export async function fetchWells(ids) {
  if (!ids.length) return null;

  try {
    //  eslint-disable-next-line no-return-await
    return await getWells({
      ids,
      fields: [
        'well.name',
        'well.settings',
        'well.company',
        'well.asset_id',
        'well.last_active_at',
        'well.program',
        'well.pad',
        'well.frac_fleet',
        'well.id',
        'well.rig',
        'rig.name',
        'rig.asset_id',
        'company.id',
        'well.status',
        'well.archivation',
        'well.lon_lat',
      ],
      include_arhivation_data: true,
    });
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function fetchRigs(ids) {
  if (!ids.length) return null;

  try {
    return await getRigs({
      ids,
      fields: [
        'rig.name',
        'rig.settings',
        'rig.asset_id',
        'rig.active_well',
        'rig.last_active_well',
        'well.name',
        'well.settings',
        'well.company',
        'rig.program',
        'well.asset_id',
        'well.last_active_at',
        'well.id',
        'well.company.id',
        'well.lon_lat',
      ],
    });
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function fetchAssetEntitiesByAssetIds(assetIds) {
  if (!assetIds.length) return null;

  try {
    return await getResolvedAssets({
      assets: assetIds,
    });
  } catch (e) {
    console.error(e);
    return null;
  }
}

const getWellObjectFromWellData = (well, rig, interventionUnit) => {
  return {
    well: well && {
      ...well.attributes,
      id: well.id,
      // NOTE: API doesn't return active child company id
      companyId: get(well, 'relationships.company.data.id'),
    },
    rig: {
      name: get(rig, 'attributes.name'),
      id: get(rig, 'id'),
      asset_id: get(rig, 'attributes.asset_id'),
    },
    program: {
      id: get(rig, 'program.id') || null,
      name: get(rig, 'program.attributes.name') || null,
    },
    ...(interventionUnit && {
      interventionUnit: {
        id: +interventionUnit.id,
        name: interventionUnit.name,
        active_well: interventionUnit.active_well,
      },
    }),
    // NOTE: Support ff/pad in mixpanel for Asset Dashboards
    completionWellInfo: {
      pad: {
        id: get(well, 'pad.id') || null,
        name: get(well, 'pad.attributes.name') || null,
      },
      fracFleet: {
        id: get(well, 'fracFleet.id') || null,
        name: get(well, 'fracFleet.attributes.name') || null,
      },
    },
  };
};

export const getAppsDataByWells = (appsWellIds, wellsById) => {
  if (!wellsById || isEmpty(wellsById)) return {};

  return appsWellIds.reduce((acc, [appId, wellId]) => {
    const well = wellsById[wellId];
    if (!well) return acc;

    acc[appId] = getWellObjectFromWellData(well, well.rig);

    return acc;
  }, {});
};

export const getAppsDataByRigs = (appsRigIds, rigsById) => {
  if (!rigsById || isEmpty(rigsById)) return {};

  return appsRigIds.reduce((acc, [appId, rigId]) => {
    const rig = rigsById[rigId];
    acc[appId] = getWellObjectFromWellData(rig?.well, rig);

    return acc;
  }, {});
};

export const getAppsDataByFFs = (appsFracFleetIds, ffsById, ffWellsData) => {
  if (!ffsById && !ffWellsData) return {};

  return appsFracFleetIds.reduce((acc, [appId, ffId]) => {
    const fracFleet = ffsById?.[ffId];
    const wells = ffWellsData?.[appId];

    acc[appId] = {
      fracFleet,
      wells,
      program: {
        id: fracFleet?.program?.id || null,
        name: fracFleet?.program?.name || null,
      },
    };

    return acc;
  }, {});
};

export const getAppsDataByPads = (appsPadIds, padWellsData) => {
  if (!appsPadIds && !padWellsData) return {};

  return appsPadIds.reduce((acc, [appId, _padId]) => {
    const wells = padWellsData?.[appId];

    acc[appId] = {
      wells,
    };

    return acc;
  }, {});
};

export const getAppsDataByInterventionUnits = (
  appsInterventionUnitIds,
  interventionUnitsById,
  appsWellIds,
  wellsById
) => {
  if (!interventionUnitsById || isEmpty(interventionUnitsById)) return {};

  return appsInterventionUnitIds.reduce((acc, [appId, interventionUnitId]) => {
    const interventionUnit = interventionUnitsById[interventionUnitId];
    const appWellId = appsWellIds.find(appWellId => appWellId[0] === appId)?.[1];
    const well = wellsById[appWellId] || interventionUnit.well;

    acc[appId] = getWellObjectFromWellData(well, well?.rig, {
      ...interventionUnit,
      ...(interventionUnit.well && {
        active_well: { id: interventionUnit.well.id, ...interventionUnit.well.attributes },
      }),
    });

    return acc;
  }, {});
};

export async function fetchFracFleets(ids) {
  if (!ids.length) return null;

  try {
    const response = await getFracFleets({
      ids,
      fields: [
        'frac_fleet.name',
        'frac_fleet.pads',
        'frac_fleet.program',
        'frac_fleet.current_pad_id',
        'frac_fleet.pad_frac_fleets',
        'frac_fleet.pad_frac_fleet.frac_fleet_lines',
      ],
    });

    return response ? dataFormatter.deserialize(response) : [];
  } catch (e) {
    console.error(e);
    return [];
  }
}

export async function fetchInterventionUnits(ids) {
  if (!ids.length) return null;

  try {
    return await getInterventionUnits({
      ids,
      fields: ['intervention_unit.name', 'intervention_unit.type', 'intervention_unit.active_well'],
    });
  } catch (e) {
    console.error(e);
    return null;
  }
}

export async function fetchFracFleetWells(appsFFIds, apps) {
  const appsById = keyBy(apps, 'id');
  const requests = appsFFIds.map(async ([appId, fracFleetId]) => {
    const options = { fields: 'all', frac_fleet_id: fracFleetId };

    const app = appsById[appId];

    if (appId === DEV_CENTER_CLI_APP_ID) options.app_key = get(app, ['app', 'app_key']);
    else options.app_id = get(app, ['app', 'id']);
    try {
      const response = await getFracFleetWells(options);
      return response ? dataFormatter.deserialize(response) : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  });

  const responses = await Promise.all(requests);

  return responses.reduce((acc, currFFWells, i) => {
    acc[appsFFIds[i][0]] = currFFWells;
    return acc;
  }, {});
}

export async function fetchPadWells(appsPadIds, apps) {
  const appsById = keyBy(apps, 'id');
  const requests = appsPadIds.map(async ([appId, padId]) => {
    const options = { fields: 'all' };

    const app = appsById[appId];

    if (appId === DEV_CENTER_CLI_APP_ID) options.app_key = get(app, ['app', 'app_key']);
    else options.app_id = get(app, ['app', 'id']);
    try {
      const response = await getPadWells(padId, options);
      return response ? dataFormatter.deserialize(response) : [];
    } catch (e) {
      console.error(e);
      return [];
    }
  });

  const responses = await Promise.all(requests);

  return responses.reduce((acc, currFFWells, i) => {
    acc[appsPadIds[i][0]] = currFFWells;
    return acc;
  }, {});
}

export const getAppHash = app =>
  [
    app.id,
    getAppAssetSpecificTypeId(app, ASSET_TYPES.well.id),
    getAppAssetSpecificTypeId(app, ASSET_TYPES.rig.id),
    getAppAssetSpecificTypeId(app, ASSET_TYPES.frac_fleet.id),
    getAppAssetSpecificTypeId(app, ASSET_TYPES.pad.id),
    getAppAssetSpecificTypeId(app, ASSET_TYPES.intervention_unit.id),
    getAppAssetSpecificTypeId(app, 'deprecatedAssetId'),
    getAppSegment(app),
  ].join('-');

export const isAppWithSettings = app =>
  Boolean(
    getAppAssetSpecificTypeId(app, ASSET_TYPES.well.id) ||
      getAppAssetSpecificTypeId(app, ASSET_TYPES.rig.id) ||
      getAppAssetSpecificTypeId(app, ASSET_TYPES.frac_fleet.id) ||
      getAppAssetSpecificTypeId(app, ASSET_TYPES.pad.id) ||
      getAppAssetSpecificTypeId(app, ASSET_TYPES.intervention_unit.id)
  );

export const getInitialAppData = app => ({
  id: app.id,
  rig: null,
  well: null,
  fracFleet: null,
  interventionUnit: null,
  program: {
    id: null,
    name: null,
  },
  completionWellInfo: {
    fracFleet: {
      id: null,
      name: null,
    },
    pad: {
      id: null,
      name: null,
    },
  },
  wells: null,
  isLoading: true,
  appHash: getAppHash(app),
});

export const getInitialAppsDataState = apps =>
  apps.reduce(
    (acc, app) => ({
      ...acc,
      [app.id]: getInitialAppData(app),
    }),
    {}
  );

export const isAppNeedingInterventionUnitWellRefresh = app => {
  const interventionUnitId = getAppAssetSpecificTypeId(app, ASSET_TYPES.intervention_unit.id);
  const wellId = getAppAssetSpecificTypeId(app, ASSET_TYPES.well.id);
  return interventionUnitId && !wellId;
};

export const prepareDataForResolver = (apps, cache) => {
  const appsWellIds = [];
  const appsRigIds = [];
  const appsFracFleetIds = [];
  const appsInterventionUnitIds = [];
  const appsFracFleetToFetchWellsIds = [];
  const appsPadIds = [];
  const appIds = [];

  apps.forEach(app => {
    const appWellId = getAppAssetSpecificTypeId(app, ASSET_TYPES.well.id);
    const appRigId = getAppAssetSpecificTypeId(app, ASSET_TYPES.rig.id);
    const appFracFleetId = getAppAssetSpecificTypeId(app, ASSET_TYPES.frac_fleet.id);
    const appPadId = getAppAssetSpecificTypeId(app, ASSET_TYPES.pad.id);
    const appInterventionUnitId = getAppAssetSpecificTypeId(app, ASSET_TYPES.intervention_unit.id);

    const appId = app.id;
    appIds.push([appId]);

    if (appInterventionUnitId) {
      appsInterventionUnitIds.push([appId, appInterventionUnitId]);
      // NOTE: we need to fetch well for intervention unit as there is no well in intervention unit
      if (appWellId) {
        appsWellIds.push([appId, appWellId]);
      }
    } else if (appWellId) {
      appsWellIds.push([appId, appWellId]);
    } else if (appRigId) {
      appsRigIds.push([appId, appRigId]);
    } else if (appFracFleetId) {
      appsFracFleetIds.push([appId, appFracFleetId]);
      if (appPadId) {
        appsPadIds.push([appId, appPadId]);
      } else {
        appsFracFleetToFetchWellsIds.push([appId, appFracFleetId]);
      }
    }
  });

  const { rigsById, wellsById, fracFleetsById, interventionUnitsById } = cache.current;

  const uniqueWellIds = getUniqAssetIds(appsWellIds, Object.keys(wellsById));
  const uniqueRigIds = getUniqAssetIds(appsRigIds, Object.keys(rigsById));
  const uniqueFFIds = getUniqAssetIds(appsFracFleetIds, Object.keys(fracFleetsById));
  const uniqueInterventionUnitIds = getUniqAssetIds(
    appsInterventionUnitIds,
    Object.keys(interventionUnitsById)
  );

  return {
    appsWellIds,
    appsRigIds,
    appsFracFleetIds,
    appsPadIds,
    appIds,
    appsFracFleetToFetchWellsIds,
    appsInterventionUnitIds,

    uniqueWellIds,
    uniqueRigIds,
    uniqueFFIds,
    uniqueInterventionUnitIds,
  };
};
